import type * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * The three colors a theme swatch paints from. Data, not tokens: a swatch
 * previews a theme that is NOT active, so it cannot read the document's
 * custom properties — callers hand it the scheme's literal colors.
 */
export interface ThemeSwatchColors {
  accent: string;
  background: string;
  foreground: string;
}

/**
 * A theme as a TILE: its page as the fill, its accent as an "Aa".
 *
 * A tile shows the relationship a theme is chosen for — accent ON background,
 * at the size type is read — where a row of flat chips leaves the reader to
 * decode which chip is the page and which the ink.
 *
 * Data, not tokens: this previews a theme that is NOT painting, so there are no
 * custom properties to read.
 */
export function ThemeSwatchStrip({
  colors,
  border,
  className,
}: Omittable<{
  colors: ThemeSwatchColors;
  /** The theme's own hairline, when it names one. Falls back to a mix. */
  border?: string;
  className?: string;
}>): React.ReactElement {
  const hairline =
    border ?? `color-mix(in oklab, ${colors.foreground} 30%, ${colors.background})`;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex size-6 shrink-0 select-none items-center justify-center rounded-md font-semibold text-[11px] leading-none",
        className,
      )}
      data-slot="theme-swatch-strip"
      style={{
        background: colors.background,
        // The tile keeps an edge on any surface — including a white theme on a
        // white popover — and the theme's OWN hairline where it names one.
        boxShadow: `inset 0 0 0 1px ${hairline}`,
        color: colors.accent,
      }}
    >
      Aa
    </span>
  );
}


